//LabReportGrid.js
import React from "react";
import { Box, Typography, Paper } from "@mui/material";
import {
  SECTION_THEMES,
  sectionPaperSx,
  sectionHeadingSx,
  innerCardSx,
} from "./sectionTheme";

const Section = ({ title, sectionKey, children }) => (
  <Paper sx={innerCardSx(sectionKey)} elevation={0}>
    {title ? (
      <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
        {title}
      </Typography>
    ) : null}
    {children}
  </Paper>
);

const KeyValue = ({ label, value }) => (
  <Typography variant="body2" sx={{ mb: 0.5, wordBreak: "break-word" }}>
    <strong>{label}:</strong> {value || "-"}
  </Typography>
);

const LabReportGrid = ({ report }) => {
  if (!report) return null;

  const { header, metadata, specimen, Ungrouped, ...testGroups } = report;

  const METADATA_LABELS = {
    report_filed_status: "Approval Status",
    lab_report_id: "Report ID",
    received_date: "Received Date",
    issued_date: "Issue Date",
    emis_report_id: "EMIS ID",
    nhs_num: "NHS Number",
    gender: "Gender",
    age: "Age",
    report_type: "Report Type",
    _category_freetext: "Category Free Text",
  };

  return (
    <>
      <Paper elevation={3} sx={sectionPaperSx("category")}>
        <Typography variant="h5" sx={sectionHeadingSx("category")}>
          2. Report Category:{" "}
          <Box
            component="span"
            sx={{ color: SECTION_THEMES.category.catColor }}
          >
            {header?.report_categories ?? ""}
          </Box>
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            width: "100%",
            justifyContent: "space-between",
            alignItems: "stretch",
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 300 }}>
            <Section title="Patient Background" sectionKey="category">
              {Object.entries(metadata || {}).map(([key, value]) => (
                <KeyValue
                  key={key}
                  label={METADATA_LABELS[key] ?? key}
                  value={value}
                />
              ))}
            </Section>
          </Box>

          <Box sx={{ flex: 1, minWidth: 300 }}>
            <Section title="" sectionKey="category">
              {Object.entries(specimen || {}).map(([k, v]) => (
                <KeyValue key={k} label={k} value={v} />
              ))}
            </Section>
          </Box>
        </Box>

        {Ungrouped && (
          <Section title="Notes" sectionKey="category">
            {Object.entries(Ungrouped).map(([k, v]) => (
              <KeyValue
                key={k}
                label={METADATA_LABELS[k] ?? k}
                value={typeof v === "string" ? v : v?.freetext}
              />
            ))}
          </Section>
        )}
      </Paper>

      <Paper elevation={3} sx={sectionPaperSx("results")}>
        <Typography variant="h5" sx={sectionHeadingSx("results")}>
          3. Lab Result
        </Typography>

        {Object.entries(testGroups).map(([groupName, tests]) => (
          <Section key={groupName} title={groupName} sectionKey="results">
            {Object.entries(tests).map(([testName, t]) => (
              <Box key={testName} sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                  {METADATA_LABELS[testName] ?? testName}
                </Typography>
                {t?.value && <KeyValue label="Value" value={t.value} />}
                {t?.range && <KeyValue label="Range" value={t.range} />}
                {t?.abnormal_flag && (
                  <KeyValue label="Flag" value={t.abnormal_flag} />
                )}
                {t?.freetext && (
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      mt: 0.5,
                    }}
                  >
                    {t.freetext}
                  </Typography>
                )}
              </Box>
            ))}
          </Section>
        ))}
      </Paper>
    </>
  );
};

export default LabReportGrid;
